// React
import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  Fragment,
  ReactNode,
  useState
} from 'react'
// Components
import Tooltip from 'components/Tooltip'
import CalendarWithRangeDates from '../../../components/CalendarWithRangeDates'
import { GoalBadge } from '../../../components/GoalBadge'
// MaterialUI
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid
} from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {
  ArrowDownward as ArrowDownwardIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@material-ui/icons'
import { makeStyles, useTheme } from '@material-ui/core/styles'

import {
  formatDecimal,
  formatPrice,
  formatToPercentage,
  safeDivision
} from '../../../utils/format'
import {
  FUTURE_SALES_MESSAGE,
  getDisabledFieldMessage,
  getDisabledFieldReasons,
  getDisabledFieldTooltipId,
  getDisabledFieldTooltipProps,
  getReportAvailability,
  getSalesDisabledFieldTooltipProps,
  isFieldDisabled
} from '../../../utils/reportDisabledFields'

// Libs
import { format } from 'date-fns'
import 'react-day-picker/lib/style.css'

// Style
import { Calendar, HeaderStore, VisiblePerfectScrollbar } from './styles'
import { IMonthReport, IRange } from 'store/modules/report/types'
import { IStore } from 'store/modules/store/types'
import { TableHeadersProps } from '../Reports'

const useStyles = makeStyles(theme => ({
  content: {
    position: 'relative',

    padding: 0,
    '&:last-child': {
      paddingBottom: 0
    }
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 25
  },
  inner: {
    minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  column: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  sticky: {
    position: 'sticky',
    left: 0,
    background: 'white'
  }
}))

interface TableHeaderProps {
  id: string
  label: string
  minWidth: number
}

interface SortHeaderCellProps {
  header: TableHeaderProps
  sortedHeaderId: string
  sortDirection: 'asc' | 'desc'
  style?: CSSProperties
}

const SortHeaderCell = ({
  header,
  sortedHeaderId,
  sortDirection,
  style
}: SortHeaderCellProps) => {
  const isSorted = header.id === sortedHeaderId

  return (
    <TableCell
      sortDirection={isSorted ? sortDirection : false}
      style={{
        minWidth: header.minWidth,
        ...(header.label === '%' && { textAlign: 'end' }),
        ...style
      }}
    >
      {isSorted ? (
        <Box
          alignItems='center'
          component='span'
          display='inline-flex'
        >
          {header.label}
          <ArrowDownwardIcon
            aria-hidden
            style={{
              color: 'rgba(0, 0, 0, 0.54)',
              fontSize: 18,
              marginLeft: 4,
              transform: sortDirection === 'asc'
                ? 'rotate(180deg)'
                : undefined
            }}
          />
        </Box>
      ) : (
        header.label
      )}
    </TableCell>
  )
}

interface ICommissionArray {
  type?: string
  sectionIndex: number
  value: number | 'Abaixo da meta'
  start: string
  end: string
}

interface IBonusArray {
  sectionIndex: number
  value: number
}

interface ISellerTooltipInfos {
  DSR?: string | undefined
  Complemento?: string | undefined
  'Comiss\u00E3o base'?: string | undefined
  Comissão?: string | undefined
  Bônus?: string | undefined
  Piso?: string | undefined
}

interface ReportsTableProps {
  reports: IMonthReport | null
  store: IStore
  requestReports: (range: IRange) => void
  tableHeaders: TableHeadersProps
  tableOtherHeaders: TableHeaderProps[]
  range: IRange
  openCalendar: boolean
  setOpenCalendar: Dispatch<SetStateAction<boolean>>
  lastYearVariation?: number
}

export default function ReportsTable({
  reports,
  store,
  requestReports,
  tableHeaders,
  tableOtherHeaders,
  range,
  openCalendar,
  setOpenCalendar,
  lastYearVariation
}: ReportsTableProps) {
  const [selectedRow, setSelectedRow] = useState(-1)
  const [selectedOtherInputsRow, setSelectedOtherInputsRow] = useState(-1)

  const classes = useStyles()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true
  })

  const disabledFields = reports?.disabledFields ?? {}
  const {
    acDayGoalReason,
    commissionReason,
    dynamicGoalItemsReason,
    dynamicGoalSalesReason,
    goalValueReason,
    mainGoalReason,
    projectionCommissionReason,
    projectionGoalReason,
    projectionSoldReason,
    salesDataReason,
    totalItemsReason,
    totalSalesReason,
    totalSoldReason
  } = getReportAvailability(disabledFields)
  const salesValuesDisabled = Boolean(salesDataReason)
  const salesTooltipProps = getSalesDisabledFieldTooltipProps(disabledFields)
  const renderDynamicGoalCell = (
    reason: string | undefined,
    value: ReactNode,
    bold = false
  ) => {
    const content = isFieldDisabled(reason) ? '-' : value

    return (
      <TableCell {...getDisabledFieldTooltipProps(reason)}>
        {bold ? <b>{content}</b> : content}
      </TableCell>
    )
  }
  const renderDisabledFieldTooltips = () => getDisabledFieldReasons(
    disabledFields
  ).map(reason => (
    <Tooltip
      key={reason}
      id={getDisabledFieldTooltipId(reason)}
      content={<p>{getDisabledFieldMessage(reason)}</p>}
      place='bottom'
    />
  ))

  const handleDayClick = (calendarRange: IRange) => {
    if (calendarRange.from && calendarRange.to) {
      requestReports(calendarRange)
    }
  }

  // Dia do mês com dois dígitos
  const getDayMonth = (date: string) => {
    return format(new Date(date), 'dd')
  }

  // Monta o tooltip de Comissões caso seja comissão por período
  const createPeriodCommissionTooltip = (
    commission: ICommissionArray,
    index: number,
    sectionIndex: number,
    arrayLength: number
  ) => {
    return (
      <Fragment key={commission.start}>
        <ol
          style={{
            listStyleType: 'none',
            ...(index === arrayLength - 1 && {
              display: 'block',
              marginBottom: '6px'
            })
          }}
        >
          <li style={{ marginLeft: '8px' }}>
            <strong>
              {`• Período ${sectionIndex} (${getDayMonth(
                commission.start
              )} a ${getDayMonth(commission.end)})`}
            </strong>
            <ol
              style={{
                listStyleType: 'none'
              }}
            >
              <li style={{ marginLeft: '16px' }}>
                {`•  ${commission.type ? `${commission.type}:` : ''} ${
                  typeof commission.value === 'number'
                    ? formatPrice(commission.value)
                    : commission.value
                }`}
              </li>
            </ol>
          </li>
        </ol>
      </Fragment>
    )
  }

  // Monta o tooltip de Bonus caso seja comissão por período
  const createPeriodBonusTooltip = (bonus: number, sectionIndex: number) => (
    <Fragment key={sectionIndex}>
      <ol style={{ listStyleType: 'none' }}>
        <li style={{ marginLeft: '8px' }}>
          <strong>• Período {sectionIndex}: </strong> {formatPrice(bonus)}
        </li>
      </ol>
    </Fragment>
  )

  const maxLengthName = 25

  const formatSellerName = (name: string) => {
    if (name.length > maxLengthName) {
      return name.slice(0, maxLengthName) + '...'
    }

    return name
  }

  const sortsByGoalPercent = store?.config.sellersSortRanking > 0 &&
    !salesValuesDisabled &&
    !isFieldDisabled(acDayGoalReason)
  const sortedHeaderId = sortsByGoalPercent
    ? 'goalPercent'
    : salesValuesDisabled ? 'name' : 'sold'
  const sortDirection = salesValuesDisabled ? 'asc' : 'desc'
  const otherSortedHeaderId = salesValuesDisabled ? 'name' : 'sold'

  return (
    <div className={classes.container}>
      {renderDisabledFieldTooltips()}
      {salesValuesDisabled && (
        <Tooltip
          id='report-disabled-sales'
          content={<p>{FUTURE_SALES_MESSAGE}</p>}
          place='bottom'
        />
      )}
      <Card>
        <Grid item>
          {openCalendar && (
            <Calendar isDesktop={isDesktop}>
              <CalendarWithRangeDates
                openCalendar={openCalendar}
                setOpenCalendar={setOpenCalendar}
                handleDayClick={handleDayClick}
                range={range}
              />
            </Calendar>
          )}
        </Grid>
        <CardContent className={classes.content}>
          {store?.otherInputs && (
            <HeaderStore>Desempenho vendedores - Todas as lojas</HeaderStore>
          )}
          <VisiblePerfectScrollbar>
            <div className={classes.inner}>
              <Table
                stickyHeader
                onMouseLeave={() => {
                  if (selectedRow > -1) {
                    setSelectedRow(-1)
                  }
                }}
              >
                <TableHead>
                  <TableRow
                    onMouseEnter={() => {
                      if (selectedRow > -1) {
                        setSelectedRow(-1)
                      }
                    }}
                  >
                    {tableHeaders.map((header, index) => (
                      <SortHeaderCell
                        header={header}
                        key={header.id}
                        sortedHeaderId={sortedHeaderId}
                        sortDirection={sortDirection}
                        style={index === 0 ? { zIndex: 10 } : undefined}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!store?.otherInputs && reports?.store && (
                    <>
                      <TableRow
                        hover
                        selected
                        onMouseEnter={() => {
                          if (selectedRow > -1) {
                            setSelectedRow(-1)
                          }
                        }}
                      >
                        <TableCell
                          className={classes.sticky}
                          style={{ backgroundColor: '#fdf8ec' }}
                        >
                          <b>Loja</b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled ? '-' : <Box
                            display='flex'
                            alignItems='center'
                            component='span'
                            style={{ gap: '8px' }}
                          >
                            <b>{formatPrice(reports.store.total.sold)}</b>
                            {typeof lastYearVariation === 'number' && (
                              <GoalBadge variation={lastYearVariation} />
                            )}
                          </Box>}
                        </TableCell>

                        <TableCell
                          {...(salesValuesDisabled
                            ? salesTooltipProps
                            : getDisabledFieldTooltipProps(acDayGoalReason))}
                          style={{ paddingRight: 0, textAlign: 'end' }}
                        >
                          <b>
                            {salesValuesDisabled || isFieldDisabled(acDayGoalReason)
                              ? '-'
                              : formatToPercentage(
                                  safeDivision(
                                    reports.store.total.sold,
                                    reports.store.metric.acDayGoal
                                  ) * 100
                                )}
                          </b>
                        </TableCell>
                        <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                          <b>
                            {isFieldDisabled(acDayGoalReason)
                              ? '-'
                              : formatPrice(reports.store.metric.acDayGoal)}
                          </b>
                        </TableCell>
                        <TableCell
                          {...(salesValuesDisabled
                            ? salesTooltipProps
                            : getDisabledFieldTooltipProps(mainGoalReason))}
                          style={{ paddingRight: 0, textAlign: 'end' }}
                        >
                          <b>
                            {salesValuesDisabled || isFieldDisabled(mainGoalReason)
                              ? '-'
                              : formatToPercentage(
                                  safeDivision(
                                    reports.store.total.sold,
                                    reports.store.metric.mainGoal
                                  ) * 100
                                )}
                          </b>
                        </TableCell>
                        <TableCell {...getDisabledFieldTooltipProps(mainGoalReason)}>
                          <b>
                            {isFieldDisabled(mainGoalReason)
                              ? '-'
                              : formatPrice(reports.store.metric.mainGoal)}
                          </b>
                        </TableCell>
                        <TableCell>
                          -
                        </TableCell>
                        <TableCell {...getDisabledFieldTooltipProps(projectionSoldReason)}>
                          <b>
                            {isFieldDisabled(projectionSoldReason)
                              ? '-'
                              : formatPrice(reports.store.projection.sold)}
                          </b>
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>{salesValuesDisabled ? '-' : reports.store.total.sales.toFixed(0)}</b>
                        </TableCell>
                        {renderDynamicGoalCell(
                          dynamicGoalSalesReason,
                          Math.ceil(reports.store.dynamicGoal.sales),
                          true
                        )}
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesValuesDisabled ? '-' : formatPrice(
                              safeDivision(
                                reports.store.total.sold,
                                reports.store.total.sales
                              )
                            )}
                          </b>
                        </TableCell>
                        <TableCell>
                          <b>{formatPrice(reports.store.dynamicGoal.ticket)}</b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesValuesDisabled ? '-' : formatDecimal(safeDivision(
                              reports.store.total.items,
                              reports.store.total.sales
                            ))}
                          </b>
                        </TableCell>
                        <TableCell>
                          <b>{formatDecimal(reports.store.dynamicGoal.itemsSale)}</b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>
                            {salesValuesDisabled ? '-' : formatPrice(
                              safeDivision(
                                reports.store.total.sold,
                                reports.store.total.items
                              )
                            )}
                          </b>
                        </TableCell>
                        <TableCell>
                          <b>{formatPrice(reports.store.dynamicGoal.price)}</b>
                        </TableCell>
                        <TableCell {...salesTooltipProps}>
                          <b>{salesValuesDisabled ? '-' : reports.store.total.items.toFixed(0)}</b>
                        </TableCell>
                        {renderDynamicGoalCell(
                          dynamicGoalItemsReason,
                          Math.ceil(reports.store.dynamicGoal.items),
                          true
                        )}
                      </TableRow>
                    </>
                  )}
                  {reports && reports.sellers.length > 0 ? (
                    [...reports.sellers]
                      .sort((a, b) => {
                        const byName = a.name.complete.localeCompare(
                          b.name.complete
                        )
                        const bySold = salesValuesDisabled
                          ? byName
                          : b.total.sold - a.total.sold || byName

                        if (
                          store?.config.sellersSortRanking > 0 &&
                          !salesValuesDisabled &&
                          !isFieldDisabled(acDayGoalReason)
                        ) {
                          const byGoal = safeDivision(
                            b.total.sold,
                            b.metric.acDayGoal
                          ) * 100 - safeDivision(
                            a.total.sold,
                            a.metric.acDayGoal
                          ) * 100

                          return byGoal || bySold
                        }

                        return bySold
                      })
                      .map((seller, index) => {
                        const commissionDisabled = isFieldDisabled(
                          commissionReason
                        )
                        const projectionCommissionDisabled = isFieldDisabled(
                          projectionCommissionReason
                        )
                        const currentCommission = commissionDisabled
                          ? undefined
                          : seller.commission
                        const {
                          complement,
                          dsr,
                          sumCommission,
                          sumSectionsCommissions,
                          salary,
                          bonus,
                          commission,
                          fixedCommission,
                          sections
                        } = currentCommission || {}

                        const isPeriodGoal = sections && sections.length > 0
                        const commissionArray: ICommissionArray[] = []
                        const bonusArray: IBonusArray[] = []

                        if (isPeriodGoal && sections) {
                          sections.forEach(section => {
                            if (section.bonus) {
                              bonusArray.push({
                                value: section.bonus.value,
                                sectionIndex: section.sectionIndex
                              })
                            }
                            if (section.start && section.end) {
                              let commissionValue: 'Abaixo da meta' | number =
                                section.commission?.value ?? 0
                              if (section.fixedCommission) {
                                commissionValue = section.fixedCommission.value
                              } else if (
                                // Caso o sumCommission seja 0 e NÃO tem fixedCommission a gente mostra Abaixo da Meta
                                section.sumCommission === 0 &&
                                !section.fixedCommission
                              ) {
                                commissionValue = 'Abaixo da meta'
                              }

                              commissionArray.push({
                                type:
                                  section.fixedCommission && 'Comissão Base',
                                sectionIndex: section.sectionIndex + 1,
                                value: commissionValue,
                                start: section.start,
                                end: section.end
                              })
                            }
                          })
                        }

                        const sellerTooltipInfos: ISellerTooltipInfos = {
                          ...(isPeriodGoal
                            ? // Objeto caso sejam vários períodos
                              {
                                ...(dsr && { DSR: formatPrice(dsr) }),
                                ...(salary && { Piso: formatPrice(salary) }),
                                ...(complement && {
                                  Complemento: formatPrice(complement)
                                })
                              }
                            : // Objeto caso seja período único
                              {
                                ...(fixedCommission?.value && {
                                  'Comissão base': formatPrice(
                                    fixedCommission.value
                                  )
                                }),
                                ...(commission?.value && {
                                  Comissão: formatPrice(commission.value)
                                }),
                                ...(dsr && { DSR: formatPrice(dsr) }),
                                ...(salary && { Piso: formatPrice(salary) }),
                                ...(complement && {
                                  Complemento: formatPrice(complement)
                                }),
                                ...(bonus?.value && {
                                  Bônus: formatPrice(bonus.value)
                                })
                              })
                        }

                        const projectedCommission = projectionCommissionDisabled
                          ? undefined
                          : seller.projection.commission
                        const {
                          complement: projectionComplement,
                          dsr: projectionDsr,
                          sumCommission: projectionSumCommission,
                          sumSectionsCommissions:
                            projectionSumSectionsCommissions,
                          salary: projectionSalary,
                          bonus: projectionBonus,
                          commission: projectionCommission,
                          fixedCommission: projectionFixedCommission,
                          sections: projectionSections
                        } = projectedCommission || {}

                        const projectionCommissionArray: ICommissionArray[] = []
                        const projectionBonusArray: IBonusArray[] = []
                        const isProjectionPeriodGoal =
                          projectionSections && projectionSections.length > 0

                        if (isProjectionPeriodGoal && projectionSections) {
                          projectionSections.forEach(section => {
                            if (section.bonus) {
                              projectionBonusArray.push({
                                value: section.bonus.value,
                                sectionIndex: section.sectionIndex
                              })
                            }
                            if (section.start && section.end) {
                              let commissionValue: 'Abaixo da meta' | number =
                                section.commission?.value ?? 0

                              if (section.fixedCommission) {
                                commissionValue = section.fixedCommission.value
                              } else if (
                                // Caso o sumCommission seja 0 e NÃO tem fixedCommission a gente mostra Abaixo da Meta
                                section.sumCommission === 0 &&
                                !section.fixedCommission
                              ) {
                                commissionValue = 'Abaixo da meta'
                              }

                              projectionCommissionArray.push({
                                type:
                                  section.fixedCommission && 'Comissão Base',
                                sectionIndex: section.sectionIndex + 1,
                                value: commissionValue,
                                start: section.start,
                                end: section.end
                              })
                            }
                          })
                        }

                        const sellerProjectionTooltipInfos = {
                          ...(isProjectionPeriodGoal
                            ? // Objeto caso sejam vários períodos
                              {
                                ...(projectionDsr && {
                                  DSR: formatPrice(projectionDsr)
                                }),
                                ...(projectionSalary && {
                                  Piso: formatPrice(projectionSalary)
                                }),
                                ...(projectionComplement && {
                                  Complemento: formatPrice(projectionComplement)
                                })
                              }
                            : // Objeto caso seja período único
                              {
                                ...(projectionFixedCommission?.value && {
                                  'Comissão base': formatPrice(
                                    projectionFixedCommission.value
                                  )
                                }),
                                ...(projectionCommission?.value && {
                                  Comissão: formatPrice(
                                    projectionCommission.value
                                  )
                                }),
                                ...(projectionDsr && {
                                  DSR: formatPrice(projectionDsr)
                                }),
                                ...(projectionSalary && {
                                  Piso: formatPrice(projectionSalary)
                                }),

                                ...(projectionComplement && {
                                  Complemento: formatPrice(projectionComplement)
                                }),
                                ...(projectionBonus?.value && {
                                  Bônus: formatPrice(projectionBonus.value)
                                })
                              })
                        }

                        const sellerName = formatSellerName(
                          seller.name.complete
                        )

                        return (
                          <TableRow
                            hover
                            key={seller._id}
                            selected={false}
                            onMouseEnter={() => setSelectedRow(index)}
                          >
                            <TableCell
                              className={classes.sticky}
                              style={{
                                ...(index === selectedRow && {
                                  backgroundColor: '#f5f5f5'
                                }),
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {seller.active ? (
                                sellerName
                              ) : (
                                <div
                                  aria-label='view'
                                  color='secondary'
                                  data-tip
                                  data-for={`view-activeGoal-modal`}
                                >
                                  <s>{sellerName}</s>
                                </div>
                              )}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesValuesDisabled ? '-' : formatPrice(seller.total.sold)}
                            </TableCell>

                            <TableCell
                              {...(salesValuesDisabled
                                ? salesTooltipProps
                                : getDisabledFieldTooltipProps(acDayGoalReason))}
                              style={{
                                color: salesValuesDisabled || isFieldDisabled(acDayGoalReason)
                                  ? 'inherit'
                                  : (value =>
                                      value >= 100
                                        ? '#27ae60'
                                        : value >= 80
                                        ? '#f39c12'
                                        : '#e74c3c')(
                                      safeDivision(
                                        seller.total.sold,
                                        seller.metric.acDayGoal
                                      ) * 100
                                    ),
                                paddingRight: 0,
                                textAlign: 'end'
                              }}
                            >
                              {salesValuesDisabled || isFieldDisabled(acDayGoalReason) ||
                              !(seller.active && seller.metric.acDayGoal > 0)
                                ? '-'
                                : formatToPercentage(
                                    safeDivision(
                                      seller.total.sold,
                                      seller.metric.acDayGoal
                                    ) * 100
                                  )}
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                              {isFieldDisabled(acDayGoalReason)
                                ? '-'
                                : formatPrice(seller.metric.acDayGoal)}
                            </TableCell>
                            <TableCell
                              {...(salesValuesDisabled
                                ? salesTooltipProps
                                : getDisabledFieldTooltipProps(goalValueReason))}
                              style={{
                                color: salesValuesDisabled || isFieldDisabled(goalValueReason)
                                  ? 'inherit'
                                  : (value =>
                                      value >= 100
                                        ? '#27ae60'
                                        : value >= 80
                                        ? '#f39c12'
                                        : '#e74c3c')(
                                      safeDivision(
                                        seller.total.sold,
                                        seller.metric.goalValue
                                      ) * 100
                                    ),
                                paddingRight: 0,
                                textAlign: 'end'
                              }}
                            >
                              {salesValuesDisabled || isFieldDisabled(goalValueReason) ||
                              !(seller.active && seller.metric.goalValue > 0)
                                ? '-'
                                : formatToPercentage(
                                    safeDivision(
                                      seller.total.sold,
                                      seller.metric.goalValue
                                    ) * 100
                                  )}
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(goalValueReason)}>
                              {isFieldDisabled(goalValueReason)
                                ? '-'
                                : formatPrice(seller.metric.goalValue)}
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(projectionGoalReason)}>
                              {isFieldDisabled(projectionGoalReason)
                                ? '-'
                                : seller.projection.goal.name}
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(projectionSoldReason)}>
                              <b>
                                {isFieldDisabled(projectionSoldReason)
                                  ? '-'
                                  : formatPrice(seller.projection.sold)}
                              </b>
                            </TableCell>
                            <TableCell {...getDisabledFieldTooltipProps(commissionReason)}>
                              {!commissionDisabled && (
                                <Tooltip
                                  id={`current-remuneration-${seller._id}`}
                                  content={
                                    <div>
                                      <strong
                                        style={{
                                          display: 'block',
                                          marginBottom: '6px',
                                          fontSize: '14px'
                                        }}
                                      >
                                        Remuneração Atual
                                      </strong>
                                      {commissionArray.length > 0 && (
                                        <>
                                          <strong>Comissão:</strong>
                                          {commissionArray.map(
                                            (commission, index) =>
                                              createPeriodCommissionTooltip(
                                                commission,
                                                index,
                                                commission.sectionIndex,
                                                commissionArray.length
                                              )
                                          )}
                                        </>
                                      )}
                                      {Object.entries(sellerTooltipInfos).map(
                                        ([key, value]) => (
                                          <p
                                            key={key}
                                            style={{ marginBottom: '2px' }}
                                          >
                                            <strong>{key}: </strong> {value}
                                          </p>
                                        )
                                      )}

                                      {bonusArray.length > 0 && (
                                        <>
                                          <strong>Bônus:</strong>
                                          {bonusArray.map(bonus =>
                                            createPeriodBonusTooltip(
                                              bonus.value,
                                              bonus.sectionIndex + 1
                                            )
                                          )}
                                        </>
                                      )}

                                      <p style={{ marginTop: '6px' }}>
                                        <strong>Total: </strong>
                                        {formatPrice(
                                          (sumCommission ||
                                            sumSectionsCommissions) ??
                                            0
                                        )}
                                      </p>
                                    </div>
                                  }
                                  place='bottom'
                                />
                              )}

                              {commissionDisabled
                                ? '-'
                                : formatPrice(
                                    (sumCommission || sumSectionsCommissions) ??
                                      0
                                  )}

                              {!commissionDisabled && (
                                <ErrorOutlineIcon
                                  data-tip
                                  data-for={`current-remuneration-${seller._id}`}
                                  style={{
                                    color: '#C4C4C4',
                                    fontSize: 20,
                                    marginLeft: 5,
                                    marginBottom: -5
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell
                              {...getDisabledFieldTooltipProps(
                                projectionCommissionReason
                              )}
                            >
                              {!projectionCommissionDisabled && (
                                <Tooltip
                                  id={`projection-remuneration-${seller._id}`}
                                  content={
                                    <div>
                                      <strong
                                        style={{
                                          display: 'block',
                                          marginBottom: '6px',
                                          fontSize: '14px'
                                        }}
                                      >
                                        Projeção de Remuneração <br />
                                      </strong>

                                      {projectionCommissionArray.length > 0 && (
                                        <>
                                          <strong>Comissão:</strong>
                                          {projectionCommissionArray.map(
                                            (commission, index) =>
                                              createPeriodCommissionTooltip(
                                                commission,
                                                index,
                                                index + 1,
                                                projectionCommissionArray.length
                                              )
                                          )}
                                        </>
                                      )}

                                      {Object.entries(
                                        sellerProjectionTooltipInfos
                                      ).map(([key, value]) => (
                                        <p
                                          key={key}
                                          style={{ marginBottom: '2px' }}
                                        >
                                          <strong>{key}: </strong> {value}
                                          <br />
                                        </p>
                                      ))}

                                      {projectionBonusArray.length > 0 && (
                                        <>
                                          <strong>Bônus:</strong>
                                          {projectionBonusArray.map(bonus =>
                                            createPeriodBonusTooltip(
                                              bonus.value,
                                              bonus.sectionIndex + 1
                                            )
                                          )}
                                        </>
                                      )}

                                      <p style={{ marginTop: '6px' }}>
                                        <strong>Total: </strong>
                                        {formatPrice(
                                          (projectionSumCommission ||
                                            projectionSumSectionsCommissions) ??
                                            0
                                        )}
                                      </p>
                                    </div>
                                  }
                                  place='bottom'
                                />
                              )}
                              <b>
                                {projectionCommissionDisabled
                                  ? '-'
                                  : formatPrice(
                                      (projectionSumCommission ||
                                        projectionSumSectionsCommissions) ??
                                        0
                                    )}

                                {!projectionCommissionDisabled && (
                                  <ErrorOutlineIcon
                                    data-tip
                                    data-for={`projection-remuneration-${seller._id}`}
                                    style={{
                                      color: '#C4C4C4',
                                      fontSize: 20,
                                      marginLeft: 5,
                                      marginBottom: -5
                                    }}
                                  />
                                )}
                              </b>
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesValuesDisabled ? '-' : seller.total.sales.toFixed(0)}
                            </TableCell>
                            {renderDynamicGoalCell(
                              dynamicGoalSalesReason,
                              Math.ceil(seller.dynamicGoal.sales)
                            )}
                            <TableCell {...salesTooltipProps}>
                              {salesValuesDisabled ? '-' : formatPrice(
                                safeDivision(
                                  seller.total.sold,
                                  seller.total.sales
                                )
                              )}
                            </TableCell>
                            <TableCell>
                              {formatPrice(seller.dynamicGoal.ticket)}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesValuesDisabled ? '-' : formatDecimal(safeDivision(
                                seller.total.items,
                                seller.total.sales
                              ))}
                            </TableCell>
                            <TableCell>
                              {formatDecimal(seller.dynamicGoal.itemsSale)}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                             {salesValuesDisabled ? '-' : formatPrice(
                                safeDivision(
                                  seller.total.sold,
                                  seller.total.items
                                )
                              )}
                            </TableCell>
                            <TableCell>
                              {formatPrice(seller.dynamicGoal.price)}
                            </TableCell>
                            <TableCell {...salesTooltipProps}>
                              {salesValuesDisabled ? '-' : seller.total.items.toFixed(0)}
                            </TableCell>
                            {renderDynamicGoalCell(
                              dynamicGoalItemsReason,
                              Math.ceil(seller.dynamicGoal.items)
                            )}
                          </TableRow>
                        )
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                        <Typography variant='subtitle2'>
                          Sem relatório para apresentar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!store?.otherInputs && reports?.other && (
                    <TableRow
                      hover
                      onMouseEnter={() => {
                        if (selectedRow > -1) {
                          setSelectedRow(-1)
                        }
                      }}
                    >
                      <TableCell>Outras vendas</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesValuesDisabled ? '-' : formatPrice(reports.other.sold)}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesValuesDisabled ? '-' : reports.other.sales.toFixed(0)}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesValuesDisabled ? '-' : formatPrice(safeDivision(
                          reports.other.sold,
                          reports.other.sales
                        ))}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesValuesDisabled ? '-' : safeDivision(
                          reports.other.items,
                          reports.other.sales
                        ).toFixed(2).replace('.', ',')}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesValuesDisabled ? '-' : formatPrice(safeDivision(
                          reports.other.sold,
                          reports.other.items
                        ))}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell {...salesTooltipProps}>
                        {salesValuesDisabled ? '-' : reports.other.items.toFixed(0)}
                      </TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </VisiblePerfectScrollbar>
        </CardContent>
      </Card>

      <Card>
        {store?.otherInputs && reports?.otherInputs && (
          <>
            <HeaderStore>Desempenho Loja - {store.name}</HeaderStore>
            <VisiblePerfectScrollbar>
              <div className={classes.inner}>
                <Table
                  stickyHeader
                  onMouseLeave={() => {
                    if (selectedOtherInputsRow > -1) {
                      setSelectedOtherInputsRow(-1)
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {tableOtherHeaders.map(header => (
                        <SortHeaderCell
                          header={header}
                          key={header.id}
                          sortedHeaderId={otherSortedHeaderId}
                          sortDirection={sortDirection}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports?.otherInputs && (
                      <>
                        <TableRow
                          hover
                          selected
                          onMouseEnter={() => {
                            if (selectedOtherInputsRow > -1) {
                              setSelectedOtherInputsRow(-1)
                            }
                          }}
                        >
                          <TableCell
                            className={classes.sticky}
                            style={{ backgroundColor: '#fdf8ec' }}
                          >
                            <b>Loja</b>
                          </TableCell>
                          <TableCell {...salesTooltipProps}>
                            <b>
                              {salesValuesDisabled ? '-' : formatPrice(
                                reports.otherInputs.store.total.sold
                              )}
                            </b>
                          </TableCell>
                          <TableCell
                            {...(salesValuesDisabled
                              ? salesTooltipProps
                              : getDisabledFieldTooltipProps(acDayGoalReason))}
                            style={{ paddingRight: 0, textAlign: 'end' }}
                          >
                            {salesValuesDisabled || isFieldDisabled(acDayGoalReason) ? (
                              '-'
                            ) : (
                              <b>
                                {formatToPercentage(
                                  safeDivision(
                                    reports.otherInputs.store.total.sold,
                                    reports.store.metric.acDayGoal
                                  ) * 100
                                )}
                              </b>
                            )}
                          </TableCell>
                          <TableCell {...getDisabledFieldTooltipProps(acDayGoalReason)}>
                            <b>
                              {isFieldDisabled(acDayGoalReason)
                                ? '-'
                                : formatPrice(reports.store.metric.acDayGoal)}
                            </b>
                          </TableCell>
                          <TableCell
                            {...(salesValuesDisabled
                              ? salesTooltipProps
                              : getDisabledFieldTooltipProps(mainGoalReason))}
                            style={{ paddingRight: 0, textAlign: 'end' }}
                          >
                            {salesValuesDisabled || isFieldDisabled(mainGoalReason) ? (
                              '-'
                            ) : (
                              <b>
                                {formatToPercentage(
                                  safeDivision(
                                    reports.otherInputs.store.total.sold,
                                    reports.store.metric.mainGoal
                                  ) * 100
                                )}
                              </b>
                            )}
                          </TableCell>
                          <TableCell {...getDisabledFieldTooltipProps(mainGoalReason)}>
                            <b>
                              {isFieldDisabled(mainGoalReason)
                                ? '-'
                                : formatPrice(reports.store.metric.mainGoal)}
                            </b>
                          </TableCell>
                          <TableCell style={{ textAlign: 'center' }}>
                            <b>-</b>
                          </TableCell>

                          <TableCell {...getDisabledFieldTooltipProps(projectionSoldReason)}>
                            <b>
                              {isFieldDisabled(projectionSoldReason)
                                ? '-'
                                : formatPrice(
                                    reports.otherInputs.store.soldProjection
                                  )}
                            </b>
                          </TableCell>
                          <TableCell {...salesTooltipProps}>
                            <b>
                              {salesValuesDisabled
                                ? '-'
                                : reports.otherInputs.store.total.sales.toFixed(0)}
                            </b>
                          </TableCell>
                          {renderDynamicGoalCell(
                            dynamicGoalSalesReason,
                            Math.ceil(
                              reports.otherInputs.store.dynamicGoal.sales
                            ),
                            true
                          )}
                          <TableCell {...salesTooltipProps}>
                            <b>
                              {salesValuesDisabled ? '-' : formatPrice(
                                safeDivision(
                                  reports.otherInputs.store.total.sold,
                                  reports.otherInputs.store.total.sales
                                )
                              )}
                            </b>
                          </TableCell>
                          <TableCell>
                            <b>{formatPrice(reports.otherInputs.store.dynamicGoal.ticket)}</b>
                          </TableCell>
                          <TableCell {...salesTooltipProps}>
                            <b>
                              {salesValuesDisabled ? '-' : formatDecimal(safeDivision(
                                reports.otherInputs.store.total.items,
                                reports.otherInputs.store.total.sales
                              ))}
                            </b>
                          </TableCell>
                          <TableCell>
                            <b>{formatDecimal(reports.otherInputs.store.dynamicGoal.itemsSale)}</b>
                          </TableCell>
                          <TableCell {...salesTooltipProps}>
                            <b>
                              {salesValuesDisabled ? '-' : formatPrice(
                                safeDivision(
                                  reports.otherInputs.store.total.sold,
                                  reports.otherInputs.store.total.items
                                )
                              )}
                            </b>
                          </TableCell>
                          <TableCell>
                            <b>{formatPrice(reports.otherInputs.store.dynamicGoal.price)}</b>
                          </TableCell>
                          <TableCell {...salesTooltipProps}>
                            <b>
                              {salesValuesDisabled
                                ? '-'
                                : reports.otherInputs.store.total.items.toFixed(0)}
                            </b>
                          </TableCell>
                          {renderDynamicGoalCell(
                            dynamicGoalItemsReason,
                            Math.ceil(reports.otherInputs.store.dynamicGoal.items),
                            true
                          )}
                        </TableRow>
                      </>
                    )}
                    {reports?.sellers.length > 0 ? (
                      [...reports.sellers]
                        .sort((a, b) => {
                          const byName = a.name.complete.localeCompare(
                            b.name.complete
                          )

                          return salesValuesDisabled
                            ? byName
                            : (b.otherInputs?.sold ?? 0) -
                                (a.otherInputs?.sold ?? 0) || byName
                        })
                        .map((seller, index) => {
                          const sellerName = formatSellerName(
                            seller.name.complete
                          )
                          return (
                            <TableRow
                              hover
                              key={seller._id}
                              selected={false}
                              onMouseEnter={() =>
                                setSelectedOtherInputsRow(index)
                              }
                            >
                              <TableCell
                                className={classes.sticky}
                                style={{
                                  ...(index === selectedOtherInputsRow && {
                                    backgroundColor: '#f5f5f5'
                                  }),
                                  whiteSpace: 'nowrap',
                                  zIndex: 10
                                }}
                              >
                                {seller.active ? (
                                  sellerName
                                ) : (
                                  <div
                                    aria-label='view'
                                    color='secondary'
                                    data-tip
                                    data-for={`view-activeGoal-modal`}
                                  >
                                    <s>{sellerName}</s>
                                    <Tooltip
                                      id={`view-activeGoal-modal`}
                                      content={
                                        <p>
                                          Vendedor(a) não está participando da
                                          meta
                                        </p>
                                      }
                                      place='right'
                                    />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell {...salesTooltipProps}>
                                {salesValuesDisabled
                                  ? '-'
                                  : formatPrice(seller.otherInputs?.sold ?? 0)}
                              </TableCell>
                              <TableCell
                                {...(salesValuesDisabled
                                  ? salesTooltipProps
                                  : getDisabledFieldTooltipProps(acDayGoalReason))}
                                style={{
                                  color: salesValuesDisabled || isFieldDisabled(acDayGoalReason)
                                    ? 'inherit'
                                    : (value =>
                                        value >= 100
                                          ? '#27ae60'
                                          : value >= 80
                                          ? '#f39c12'
                                          : '#e74c3c')(
                                        safeDivision(
                                          seller.otherInputs?.sold ?? 0,
                                          seller.metric.acDayGoal
                                        ) * 100
                                      ),
                                  opacity: 0.45,
                                  paddingRight: 0,
                                  textAlign: 'end'
                                }}
                              >
                                {salesValuesDisabled || isFieldDisabled(acDayGoalReason) ||
                                !(seller.active && seller.metric.acDayGoal > 0)
                                  ? '-'
                                  : formatToPercentage(
                                      safeDivision(
                                        seller?.otherInputs?.sold ?? 0,
                                        seller.metric.acDayGoal
                                      ) * 100
                                    )}
                              </TableCell>
                              <TableCell
                                style={{ opacity: 0.45 }}
                                {...getDisabledFieldTooltipProps(acDayGoalReason)}
                              >
                                {isFieldDisabled(acDayGoalReason)
                                  ? '-'
                                  : formatPrice(seller.metric.acDayGoal)}
                              </TableCell>
                              <TableCell
                                {...(salesValuesDisabled
                                  ? salesTooltipProps
                                  : getDisabledFieldTooltipProps(goalValueReason))}
                                style={{
                                  color: salesValuesDisabled || isFieldDisabled(goalValueReason)
                                    ? 'inherit'
                                    : (value =>
                                        value >= 100
                                          ? '#27ae60'
                                          : value >= 80
                                          ? '#f39c12'
                                          : '#e74c3c')(
                                        safeDivision(
                                          seller.otherInputs?.sold ?? 0,
                                          seller.metric.goalValue
                                        ) * 100
                                      ),
                                  opacity: 0.45,
                                  paddingRight: 0,
                                  textAlign: 'end'
                                }}
                              >
                                {salesValuesDisabled || isFieldDisabled(goalValueReason) ||
                                !(seller.active && seller.metric.goalValue > 0)
                                  ? '-'
                                  : formatToPercentage(
                                      safeDivision(
                                        seller.otherInputs?.sold ?? 0,
                                        seller.metric.goalValue
                                      ) * 100
                                    )}
                              </TableCell>
                              <TableCell
                                style={{ opacity: 0.45 }}
                                {...getDisabledFieldTooltipProps(goalValueReason)}
                              >
                                {isFieldDisabled(goalValueReason)
                                  ? '-'
                                  : formatPrice(seller.metric.goalValue)}
                              </TableCell>
                              <TableCell
                                style={{ opacity: 0.45, textAlign: 'center' }}
                              >
                                -
                              </TableCell>
                              <TableCell
                                style={{ opacity: 0.45 }}
                                {...getDisabledFieldTooltipProps(
                                  projectionSoldReason
                                )}
                              >
                                {isFieldDisabled(projectionSoldReason)
                                  ? '-'
                                  : formatPrice(seller.projection.sold)}
                              </TableCell>
                              <TableCell {...salesTooltipProps}>
                                {salesValuesDisabled
                                  ? '-'
                                  : seller.otherInputs?.sales.toFixed(0) ?? 0}
                              </TableCell>
                              {renderDynamicGoalCell(
                                dynamicGoalSalesReason,
                                Math.ceil(seller.dynamicGoal.sales)
                              )}
                              <TableCell {...salesTooltipProps}>
                                {salesValuesDisabled ? '-' : formatPrice(
                                  safeDivision(
                                    seller.otherInputs?.sold ?? 0,
                                    seller.otherInputs?.sales ?? 0
                                  )
                                )}
                              </TableCell>
                              <TableCell>
                                {formatPrice(seller.dynamicGoal.ticket)}
                              </TableCell>
                              <TableCell {...salesTooltipProps}>
                                {salesValuesDisabled ? '-' : formatDecimal(safeDivision(
                                  seller.otherInputs?.items ?? 0,
                                  seller.otherInputs?.sales ?? 0
                                ))}
                              </TableCell>
                              <TableCell>
                                {formatDecimal(seller.dynamicGoal.itemsSale)}
                              </TableCell>
                              <TableCell {...salesTooltipProps}>
                                {salesValuesDisabled ? '-' : formatPrice(
                                  safeDivision(
                                    seller.otherInputs?.sold ?? 0,
                                    seller.otherInputs?.items ?? 0
                                  )
                                )}
                              </TableCell>
                              <TableCell>
                                {formatPrice(seller.dynamicGoal.price)}
                              </TableCell>
                              <TableCell {...salesTooltipProps}>
                                {salesValuesDisabled
                                  ? '-'
                                  : seller.otherInputs?.items.toFixed(0) || 0}
                              </TableCell>
                              {renderDynamicGoalCell(
                                dynamicGoalItemsReason,
                                Math.ceil(seller.dynamicGoal.items)
                              )}
                            </TableRow>
                          )
                        })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                          <Typography variant='subtitle2'>
                            Sem relatório para apresentar.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {reports?.otherInputs && (
                      // Outras vendas
                      <TableRow
                        hover
                        onMouseEnter={() => {
                          if (selectedOtherInputsRow > -1) {
                            setSelectedOtherInputsRow(-1)
                          }
                        }}
                      >
                        <TableCell>Outras vendas</TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled
                            ? '-'
                            : formatPrice(reports.otherInputs.other.sold)}
                        </TableCell>
                        <TableCell style={{ opacity: 0.45 }}>-</TableCell>
                        <TableCell style={{ opacity: 0.45 }}>-</TableCell>
                        <TableCell style={{ opacity: 0.45 }}>-</TableCell>
                        <TableCell style={{ opacity: 0.45 }}>-</TableCell>
                        <TableCell style={{ opacity: 0.45 }}>-</TableCell>
                        <TableCell style={{ opacity: 0.45 }}>-</TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled
                            ? '-'
                            : reports.otherInputs.other.sales.toFixed(0)}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled ? '-' : formatPrice(
                            safeDivision(
                              reports.otherInputs.other.sold,
                              reports.otherInputs.other.sales
                            )
                          )}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled ? '-' : formatDecimal(safeDivision(
                            reports.otherInputs.other.items,
                            reports.otherInputs.other.sales
                          ))}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled ? '-' : formatPrice(
                            safeDivision(
                              reports.otherInputs.other.sold,
                              reports.otherInputs.other.items
                            )
                          )}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell {...salesTooltipProps}>
                          {salesValuesDisabled
                            ? '-'
                            : reports.otherInputs.other.items.toFixed(0)}
                        </TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </VisiblePerfectScrollbar>
          </>
        )}
      </Card>
    </div>
  )
}
